/* import __COLOCATED_TEMPLATE__ from './joined-list.hbs.js'; */
var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _class, _class2, _descriptor, _class3;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { tagName } from '@ember-decorators/component';
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';

/**
## Joined List

Joins a list of strings with proper connectors (e.g. ", "; " and "; ", and").
If used as a standalone component, renders the complete string with connectors.
If used in block form, yields `value` and the block is used to render each list element. The connectors are included automatically.

### Example

```example
@title Basic usage

@controller-properties
{
  values: ['A', 'B', 'C']
}

@hbs
{{joined-list values}}
```

### Advanced Usage

```example
@title Advanced Usage

@controller-properties
{
  values: ['A', 'B', 'C']
}

@hbs
{{#joined-list
  values
  wordsConnector=" COMMA "
  lastWordConnector=" COMMA AND " as |value|~}}
  <span class='type-color-blue-medium'>{{value}}</span>
{{~/joined-list}}
```

### Parameters
@param {Array<String>} [values] first positional param
@param {String} [wordsConnector] connector to use between non-final elements for a list with length > 2. defaults to t('common.toSentence.wordsConnector')
@param {String} [twoWordsConnector] connector to use for a list of length === 2. defaults to t('common.toSentence.twoWordsConnector')
@param {String} [lastWordConnector] connector to use between the final two elements for a list with length > 2. defaults to t('common.toSentence.lastWordConnector')
@param {Number} [maxWordsToShow] maximum number of words to show. If there are N more words than maxWordsToShow, combine them into a single value of "N more"
*/
let JoinedList = (_dec = tagName('span'), _dec2 = computed('wordsConnector', 'i18n'), _dec3 = computed('lastWordConnector', 'i18n'), _dec4 = computed('twoWordsConnector', 'i18n'), _dec5 = computed('_lastWordConnector', '_twoWordsConnector', '_wordsConnector', 'truncatedValues'), _dec6 = computed('values.[]', 'maxWordsToShow', 'i18n'), _dec(_class = (_class2 = (_class3 = class JoinedList extends Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, 'data-test-joined-list', true);
    _initializerDefineProperty(this, "i18n", _descriptor, this);
  }
  get _wordsConnector() {
    if (this.wordsConnector) {
      return this.wordsConnector;
    }
    return this.i18n.t('common.toSentence.wordsConnector');
  }
  get _lastWordConnector() {
    if (this.lastWordConnector) {
      return this.lastWordConnector;
    }
    return this.i18n.t('common.toSentence.lastWordConnector');
  }
  get _twoWordsConnector() {
    if (this.twoWordsConnector) {
      return this.twoWordsConnector;
    }
    return this.i18n.t('common.toSentence.twoWordsConnector');
  }

  /**
   * @param {Number} index the current index
   * @param {Number} length the total number of items in the list
   * @returns {String} the connector that _precedes_ the given index
   */
  connector(index, length) {
    // return early for the trivial cases of the first element or a single-element list
    if (index === 0) {
      return '';
    }
    if (index === length - 1) {
      if (length === 2) {
        return this._twoWordsConnector;
      } else {
        return this._lastWordConnector;
      }
    } else {
      return this._wordsConnector;
    }
  }
  get parts() {
    const wordsToShow = this.truncatedValues;
    const length = wordsToShow.length;
    return wordsToShow.map((value, index) => {
      return {
        connector: this.connector(index, length),
        value
      };
    });
  }
  get truncatedValues() {
    const length = this.get('values.length'); // eslint-disable-line ember/no-get
    const max = this.maxWordsToShow;
    if (length > max) {
      const extraCount = this.i18n.t('common.toSentence.wordsRemaining', {
        count: length - max
      });
      return [...this.values.slice(0, max), extraCount];
    }
    return this.values;
  }
}, _defineProperty(_class3, "positionalParams", ['values']), _class3), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "_wordsConnector", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "_wordsConnector"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "_lastWordConnector", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "_lastWordConnector"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "_twoWordsConnector", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "_twoWordsConnector"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "parts", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "parts"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "truncatedValues", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "truncatedValues"), _class2.prototype), _class2)) || _class);
export { JoinedList as default };